// components/memberships/MembershipTypesList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Tag, 
  Typography, 
  Table, 
  Input, 
  Space, 
  Dropdown, 
  Button, 
  App, 
  Tooltip 
} from 'antd';
import { 
  MoreOutlined, 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { 
  listMembershipTypes, 
  deleteMembershipType, 
  restoreMembershipType,
  toggleMembershipTypeStatus 
} from '../../services/memberships';

const { Title } = Typography;
const { useApp } = App;

const MembershipTypesList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items } = await listMembershipTypes({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items.data || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total: items.total 
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de tipos de membresía.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    console.log('Acción:', action, 'Tipo de membresía:', record);
    
    switch (action) {
      case 'view':
        navigate(`/memberships/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/memberships/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar tipo de membresía?' : '¿Desactivar tipo de membresía?',
          content: `${record.name || 'Tipo de membresía'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await toggleMembershipTypeStatus(record.id, willActivate);
              message.success(willActivate ? 'Tipo de membresía activado' : 'Tipo de membresía desactivado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar tipo de membresía?',
          content: `"${record.name || 'Tipo de membresía'}"`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteMembershipType(record.id);
              message.success('Tipo de membresía eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el tipo de membresía');
            }
          },
        });
        break;
      case 'restore':
        modal.confirm({
          title: '¿Restaurar tipo de membresía?',
          content: `"${record.name || 'Tipo de membresía'}"`,
          okText: 'Restaurar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await restoreMembershipType(record.id);
              message.success('Tipo de membresía restaurado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo restaurar el tipo de membresía');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const isDeleted = record.deletedAt !== null && record.deletedAt !== undefined;
    
    const baseItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver detalles',
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar',
        disabled: isDeleted,
      },
    ];

    if (isDeleted) {
      // Si está eliminado, solo mostrar opción de restaurar
      baseItems.push({
        type: 'divider',
      });
      baseItems.push({
        key: 'restore',
        icon: <UndoOutlined />,
        label: 'Restaurar',
      });
    } else {
      // Si no está eliminado, mostrar opciones normales
      baseItems.push({
        type: 'divider',
      });
      baseItems.push({
        key: 'toggle',
        icon: record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      });
      baseItems.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Eliminar',
        danger: true,
      });
    }

    return {
      items: baseItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { 
      title: 'Nombre', 
      dataIndex: 'name', 
      key: 'name', 
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive, record) => {
        if (record.deletedAt) {
          return <Tag color="red">Eliminado</Tag>;
        }
        return (
          <Tag color={isActive ? 'green' : 'volcano'}>
            {isActive ? 'Activo' : 'Inactivo'}
          </Tag>
        );
      },
    },
    {
      title: 'Creado',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('es-MX');
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Tipos de Membresías</Title>}
        extra={
          <Tooltip title="Agregar tipo de membresía">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              aria-label="Agregar tipo de membresía"
              onClick={() => navigate('/memberships/add')}
            >
              Agregar
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Buscar por nombre"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default MembershipTypesList;